import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../../i18n';
import './LandingSections.css';

// ── THE HOMEPAGE'S EXPLANATORY SECTIONS ──────────────────────────────────────
// Everything between the hero and the previews: what the four steps are, what
// the 3D and AI actually do, and what a transformation looks like. All
// presentational — nothing here reads from Firestore, which is why they live
// together and away from the two preview blocks that do.
//
// WHAT THE PAGE IS ABOUT, IN ORDER. The homepage used to open on a hero and go
// straight into a filterable product catalogue, which made Tunify read as a
// furniture shop that happened to have a 3D tool. The order below says the
// opposite: the builder is the product, and the designers and the shop are
// what surrounds it.

/* ── HOW IT WORKS ──────────────────────────────────────────────────────────
   Four steps, numbered, no pictures. The numbers ARE the illustration — a
   photograph beside each one would say nothing the sentence does not, and
   four more images on the first scroll is a real cost for a decoration. */
const STEPS = [
  { n: 1, k: 'lnd_step1_label', s: 'lnd_step1_sub' },
  { n: 2, k: 'lnd_step2_label', s: 'lnd_step2_sub' },
  { n: 3, k: 'lnd_step3_label', s: 'lnd_step3_sub' },
  { n: 4, k: 'lnd_step4_label', s: 'lnd_step4_sub' },
];

export function LandingSteps() {
  const { t } = useT();
  return (
    <section className="lsx lsx-steps">
      <div className="lsx-head">
        <h2 className="lsx-h2">{t('lnd_steps_title')}</h2>
        <p className="lsx-sub">{t('lnd_steps_sub')}</p>
      </div>
      <ol className="lsx-steps-row">
        {STEPS.map(s => (
          <li className="lsx-step" key={s.n}>
            <span className="lsx-step-n">{s.n}</span>
            <h3 className="lsx-step-t">{t(s.k)}</h3>
            <p className="lsx-step-b">{t(s.s)}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

/* ── 3D + AI SHOWCASE ──────────────────────────────────────────────────────
   Two cards, each carrying a real screenshot of the thing it describes rather
   than a stock photograph of a room: banner3 is the floor-plan editor with its
   3D preview, banner2 is a 3D scene beside the photoreal image the AI returned
   from it. Both already ship with the app (the dashboard banner uses them), so
   this section costs no new bytes.

   The third card has no screenshot — "find a designer" is a directory, and a
   picture of a list is not worth 200KB — so it is text on the same card shape
   and sits beside the two that do. */
export function LandingFeatures() {
  const { t } = useT();
  return (
    <section className="lsx lsx-feat">
      <div className="lsx-head">
        <p className="lsx-kicker">{t('lnd_feat_kicker')}</p>
        <h2 className="lsx-h2">{t('lnd_feat_title')}</h2>
      </div>

      <div className="lsx-feat-grid">
        <article className="lsx-card lsx-card--wide">
          <div className="lsx-shot">
            {/* CONTENT, NOT DECORATION: this is the feature the card is about,
                so it gets described. The text says what is actually in the
                picture — the editor with a measured room and the 3D preview
                beside it — rather than a list of the phrases we would like to
                rank for. Each of the two cards gets its own sentence for the
                same reason. */}
            <img
              src="/images/banner/banner3.webp"
              alt="The Tunify floor plan editor with a room drawn to 12 by 8 metres on a grid, and a 3D preview of the finished room beside it"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="lsx-card-body">
            <h3 className="lsx-card-t">{t('lnd_feat2_title')}</h3>
            <p className="lsx-card-b">{t('lnd_feat2_desc')}</p>
          </div>
        </article>

        <article className="lsx-card lsx-card--wide">
          <div className="lsx-shot">
            <img
              src="/images/banner/banner2.webp"
              alt="A 3D room view side by side with the photorealistic AI render generated from it"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="lsx-card-body">
            <h3 className="lsx-card-t">{t('lnd_feat1_title')}</h3>
            <p className="lsx-card-b">{t('lnd_feat1_desc')}</p>
          </div>
        </article>

        {/* The "Find a designer" card was here. Removed on request — the
            designers preview further down the page already makes the same point
            with real profiles and its own "View all designers" link, so this was
            the same offer stated twice on one screen.

            The grid needs no adjustment: it is two columns and this card was the
            only `--flat` one, spanning `1 / -1` on a row of its own. The two
            remaining cards still fill row one exactly.

            `lnd_feat3_title` / `lnd_feat3_desc` are left in the locale files
            rather than deleted, so the card can be restored without rewriting
            copy in three languages. */}
      </div>
    </section>
  );
}

/* ── BEFORE → AFTER ────────────────────────────────────────────────────────
   The two transformations the app actually performs, switchable.

   NOT A DRAG SLIDER, and this is a limitation of the assets rather than a
   design choice: both images are single files with the split already baked
   into the pixels — they arrive as one picture of a before beside an after.
   A reveal handle dragged over one would move a seam that is part of the
   photograph, which reads as broken. Selecting between them is the honest
   interaction the material supports; a real slider needs the halves as two
   separate files.

   ONLY TWO, because only two such pairs exist in the app. A third card would
   have to be an interior photograph relabelled as a transformation. */
const SHOTS = [
  { id: 'stage', src: '/images/banner/banner1.webp', t: 'lnd_tr1_t', b: 'lnd_tr1_b' },
  { id: 'render', src: '/images/banner/banner2.webp', t: 'lnd_tr2_t', b: 'lnd_tr2_b' },
];

export function LandingTransform() {
  const { t } = useT();
  const [at, setAt] = useState(0);
  const shot = SHOTS[at];

  return (
    <section className="lsx lsx-tr">
      <div className="lsx-head">
        <h2 className="lsx-h2">{t('lnd_tr_title')}</h2>
        <p className="lsx-sub">{t('lnd_tr_sub')}</p>
      </div>

      <div className="lsx-tr-tabs" role="tablist" aria-label={t('lnd_tr_title')}>
        {SHOTS.map((s, i) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={i === at}
            className={`lsx-tr-tab${i === at ? ' lsx-tr-tab--on' : ''}`}
            onClick={() => setAt(i)}
          >{t(s.t)}</button>
        ))}
      </div>

      <figure className="lsx-tr-stage">
        <img src={shot.src} alt={t(shot.t)} loading="lazy" decoding="async" />
        {/* ── THE HALVES, NAMED — UNDER THE PICTURE, NOT ON IT ──
            These were absolutely placed in the image's top corners, which put
            "Before" over a pot plant: the split is baked into each file at its
            own position, and one of the two frames the pair inside a wider
            room shot. No fixed pair of coordinates is right for both.

            A bar beneath, split down the middle, points at the halves without
            claiming to know where the seam is. */}
        <div className="lsx-tr-legend" aria-hidden="true">
          <span className="lsx-tr-half">{t('lnd_tr_before')}</span>
          <span className="lsx-tr-half lsx-tr-half--after">{t('lnd_tr_after')}</span>
        </div>
        <figcaption className="lsx-tr-cap">{t(shot.b)}</figcaption>
      </figure>
    </section>
  );
}

/* ── THE LAST THING ON THE PAGE ────────────────────────────────────────────
   One question and one button. Anything else here competes with the only
   action the page has been building towards. */
export function LandingFinalCta() {
  const { t } = useT();
  return (
    <section className="lsx lsx-final">
      <h2 className="lsx-final-t">{t('lnd_bottom_title')}</h2>
      <p className="lsx-final-b">{t('lnd_bottom_sub')}</p>
      <Link className="lnd-cta lnd-cta--lg" to="/try">
        {t('lnd_cta')}
        <svg viewBox="0 0 16 16" fill="none" width="16" height="16" aria-hidden="true">
          <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8"
            strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </Link>
    </section>
  );
}
